import api from './axiosInstance';
import { API_BASE_URL } from '../config';

const PROTOCOL_URL = `${API_BASE_URL}/Protocol`;
const TRANSCRIPCIONES_URL = `${API_BASE_URL}/Transcripciones`;

/**
 * Obtiene todos los protocolos agrupados por línea de negocio
 * @returns {Promise<{totals: Object, data: Array}>}
 */
export const getProtocolsGroupedByBusinessLine = async () => {
  try {
    const response = await api.get(PROTOCOL_URL);
    return response.data;
  } catch (error) {
    console.error('Error fetching protocols:', error);
    throw error;
  }
};

/**
 * Obtiene los pasos de un protocolo específico
 * @param {number} protocolId - ID del protocolo
 * @returns {Promise<Array>}
 */
export const getProtocolSteps = async (protocolId) => {
  try {
    const url = `${PROTOCOL_URL}/${protocolId}/steps`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching protocol steps:', error);
    throw error;
  }
};

/**
 * Envia una solicitud de analisis de llamadas por protocolo
 * @param {Object} payload - { protocolId, agentId, transcriptionIds, supervisorUserId, startDate?, endDate? }
 * @returns {Promise<Object>}
 */
export const analyzeProtocolCalls = async (payload) => {
  try {
    const body = {
      protocolId: payload.protocolId,
      agentId: payload.agentId,
      transcriptionIds: payload.transcriptionIds,
      supervisorUserId: payload.supervisorUserId,
    };
    if (payload.startDate) body.startDate = payload.startDate;
    if (payload.endDate) body.endDate = payload.endDate;

    const response = await api.post(
      `${TRANSCRIPCIONES_URL}/EvaluateAgentCalls`,
      body
    );
    return response.data;
  } catch (error) {
    console.error('Error analyzing protocol calls:', error);
    throw error;
  }
};

/**
 * Obtiene un protocolo por ID para edición
 * @param {number} protocolId - ID del protocolo
 * @returns {Promise<Object>}
 */
export const getProtocolById = async (protocolId) => {
  try {
    const response = await api.get(`${PROTOCOL_URL}/${protocolId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching protocol by id:', error);
    throw error;
  }
};

/**
 * Guarda/actualiza un protocolo existente
 * @param {Object} payload - { id, name, businessLineId, isEnabled, steps: [] }
 * @returns {Promise<{message: string}>}
 */
export const saveProtocol = async (payload) => {
  try {
    const response = await api.put(PROTOCOL_URL, {
      id: payload.id,
      name: payload.name,
      businessLineId: payload.businessLineId,
      isEnabled: payload.isEnabled,
      steps: payload.steps || [],
    });
    return response.data;
  } catch (error) {
    console.error('Error saving protocol:', error);
    throw error;
  }
};

/**
 * Crea un nuevo protocolo
 * @param {Object} payload - { name, businessLineId, isEnabled, usuarioCreadorId, steps: [] }
 * @returns {Promise<{message: string, protocolId: number}>}
 */
export const createProtocol = async (payload) => {
  try {
    const response = await api.post(PROTOCOL_URL, {
      name: payload.name,
      businessLineId: payload.businessLineId,
      isEnabled: payload.isEnabled,
      usuarioCreadorId: payload.usuarioCreadorId,
      steps: payload.steps || [],
    });
    return response.data;
  } catch (error) {
    console.error('Error creating protocol:', error);
    throw error;
  }
};

/**
 * Obtiene todas las líneas de negocio
 * @returns {Promise<Array>}
 */
export const getBusinessLines = async () => {
  try {
    const response = await api.get(`${PROTOCOL_URL}/businesslines`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business lines:', error);
    throw error;
  }
};

/**
 * Obtiene el historial de analisis de protocolos
 * @param {number} page
 * @param {number} pageSize
 * @param {string|null} sortBy
 * @param {boolean|null} desc
 * @param {AbortSignal} signal
 * @returns {Promise<Object>}
 */
export const getProtocolLogs = async (
  page = 1,
  pageSize = 10,
  sortBy = null,
  desc = null,
  search = null,
  signal
) => {
  try {
    const params = { page, pageSize };
    if (sortBy) params.sortBy = sortBy;
    if (desc !== null && desc !== undefined) params.desc = desc;
    if (search && String(search).trim()) params.search = String(search).trim();

    const response = await api.get(`${TRANSCRIPCIONES_URL}/GetLogProtocols`, {
      params,
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching protocol logs:', error);
    throw error;
  }
};

/**
 * Obtiene la cantidad de llamadas por agente en un rango de fechas
 * @param {Object} params - { agentId, startDate, endDate }
 * @param {AbortSignal} signal
 * @returns {Promise<number|Object>}
 */
export const getAgentCallCount = async (params, signal) => {
  try {
    const response = await api.get(`${TRANSCRIPCIONES_URL}/GetAgentCallCount`, {
      params: {
        agentId: params.agentId,
        startDate: params.startDate,
        endDate: params.endDate,
      },
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching agent call count:', error);
    throw error;
  }
};

/**
 * Obtiene el listado de llamadas por agente en un dia especifico
 * @param {Object} params - { agentId, day }
 * @param {AbortSignal} signal
 * @returns {Promise<Object>}
 */
export const getAgentCallList = async (params, signal) => {
  try {
    const response = await api.get(`${TRANSCRIPCIONES_URL}/GetAgentCallList`, {
      params: {
        agentId: params.agentId,
        day: params.day,
      },
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching agent call list:', error);
    throw error;
  }
};

/**
 * Obtiene el resumen del analisis de protocolo
 * @param {number|string} id
 * @param {AbortSignal} signal
 * @returns {Promise<Object>}
 */
export const getSummaryFeedBack = async (id, signal) => {
  try {
    const response = await api.get(
      `${TRANSCRIPCIONES_URL}/GetSummaryFeedBack`,
      {
        params: { id },
        signal,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching summary feedback:', error);
    throw error;
  }
};

export const getNoClientsProtocolAnalisys = async (callTranscriptId) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/CallTranscriptAnalysis/GetByCallTranscriptId/${callTranscriptId}`,
      null
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching summary feedback:', error);
    throw error;
  }
};

/**
 * Obtiene el detalle por llamada de un analisis de protocolo
 * @param {number|string} id
 * @param {AbortSignal} signal
 * @returns {Promise<Array>}
 */
export const getCallList = async (id, signal) => {
  try {
    const response = await api.get(`${TRANSCRIPCIONES_URL}/GetCallList`, {
      params: { id },
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching call list:', error);
    throw error;
  }
};

/**
 * Obtiene el detalle de una llamada analizada
 * @param {number|string} callId
 * @param {AbortSignal} signal
 * @returns {Promise<Object>}
 */
export const getCallSpecific = async (callId, signal) => {
  try {
    const response = await api.get(`${TRANSCRIPCIONES_URL}/GetCallSpecific`, {
      params: { callId },
      signal,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching call specific:', error);
    throw error;
  }
};
